'use client';

import { useLayoutEffect, useState } from 'react';

export type ImageModel = {
  image?: HTMLImageElement;
  isLoaded: boolean;
  isError: boolean;
  dimensions?: { width: number; height: number };
};
/**
 * This hook gives loading, loaded, error states for image
 * @param {String} src - Image source
 */

export const useImage = (src?: string): ImageModel => {
  const [image, setImage] = useState<HTMLImageElement | undefined>(undefined);
  const [isLoaded, setIsLoaded] = useState<boolean>(!src);
  const [isError, setIsError] = useState<boolean>(false);
  const [dimensions, setDimensions] = useState<{ width: number; height: number } | undefined>(
    undefined,
  );

  useLayoutEffect(() => {
    const img: HTMLImageElement = new Image();

    const handleError = () => {
      setIsError(true);
    };

    const handleLoad = () => {
      setImage(img);
      setIsLoaded(true);
      setIsError(false);
      setDimensions({ width: img.naturalWidth, height: img.naturalHeight });
    };

    if (src) {
      setImage(undefined);
      setIsLoaded(false);
      setIsError(false);
      setDimensions(undefined);

      img.src = src;
      img.addEventListener('error', handleError);
      img.addEventListener('load', handleLoad);
    }

    return () => {
      img.removeEventListener('error', handleError);
      img.removeEventListener('load', handleLoad);
    };
  }, [src]);

  return {
    image,
    isLoaded,
    isError,
    dimensions,
  };
};

export default useImage;
